import { Parser, expr as exprSource, type Dim, type Expr, type Unit } from '@gymcloud/mixedscript'
import { useRef, useState, type ReactNode } from 'react'

import { staticExprProblem } from '~/lib/expr-guard'
import { useSelectOnFocus } from '~/lib/select-on-focus'

import { FloatOut } from './float-out'

import { Popover, PopoverAnchor, PopoverContent } from './ui/popover'

/**
 * The number fields of the template editor. Each one holds a slot of the script — a weight, a
 * rest, a count — and what it commits is an expression the script can actually carry: garbage
 * never lands in the tree, and a field that disagrees with the tree settles back to it on blur.
 */

/** A number written plainly, with no letters riding on it. It takes the field's own unit. */
const PLAIN = /^\d*\.?\d+$/

interface Checked {
  readonly ok: boolean
  readonly value?: Expr | null
}

/** Whether an expression carries a number of the dimension the field is for. */
function carries(value: Expr, dim: Dim): boolean {
  switch (value.type) {
    case 'number':
      return value.unit.dim === dim
    case 'unary':
      return carries(value.operand, dim)
    case 'binary':
      return carries(value.left, dim) || carries(value.right, dim)
    case 'ternary':
      return carries(value.then, dim) || carries(value.otherwise, dim)
    // A call works its own value out — e1rm gives a weight whatever its arguments wear.
    case 'call':
      return true
    default:
      return false
  }
}

/** `8-12` and `45s-60s`: a range written the way people write ranges. */
const DASHED = /^\s*(\d+(?:\.\d+)?[a-z]*)\s*[-–]\s*(\d+(?:\.\d+)?[a-z]*)\s*$/
/** `5+`: at least five, and as many more as there are. */
const PLUSSED = /^\s*(\d+(?:\.\d+)?)\s*\+\s*$/

/** What the typed text means for this field, or `ok: false` while it means nothing yet. */
function checked(text: string, unit: Unit, clearable: boolean, sugared: boolean): Checked {
  const trimmed = text.trim()
  if (trimmed === '') {
    return clearable ? { ok: true, value: null } : { ok: false }
  }

  // Where a range can stand, `8-12` IS a range — never eight minus twelve — and it climbs.
  let written = PLAIN.test(trimmed) ? trimmed + unit.suffix : trimmed
  if (sugared) {
    const dashed = DASHED.exec(trimmed)
    if (dashed !== null) {
      const low = PLAIN.test(dashed[1] ?? '')
        ? `${dashed[1] ?? ''}${unit.suffix}`
        : (dashed[1] ?? '')
      const high = PLAIN.test(dashed[2] ?? '')
        ? `${dashed[2] ?? ''}${unit.suffix}`
        : (dashed[2] ?? '')
      written = `range(${low}, ${high})`
    } else {
      // `5+` means at least five, which only reps can mean — a weight has no amrap.
      const plussed = unit.dim === 'none' ? PLUSSED.exec(trimmed) : null
      if (plussed !== null) {
        written = `amrap(${plussed[1] ?? ''})`
      }
    }
  }

  let value: Expr
  try {
    value = new Parser(written).parseExpr()
  } catch {
    return { ok: false }
  }
  if (staticExprProblem(value) !== null) {
    return { ok: false }
  }
  // A weight or a rest written without its unit would evaluate as something else entirely.
  if (unit.dim !== 'none' && !carries(value, unit.dim)) {
    return { ok: false }
  }
  // Nobody plans a hundred thousand of anything; a number that size is a slipped key, and
  // some of them are big enough to knock the evaluator over.
  if (value.type === 'number' && (value.value < 0 || value.value > 100000)) {
    return { ok: false }
  }
  // One literal minus another is a range typed where none can stand, near enough to always.
  if (
    value.type === 'binary' &&
    value.op === '-' &&
    value.left.type === 'number' &&
    value.right.type === 'number'
  ) {
    return { ok: false }
  }
  // A range runs upward or it is a typo.
  if (
    value.type === 'call' &&
    value.callee.type === 'name' &&
    value.callee.name === 'range' &&
    value.args[0]?.type === 'number' &&
    value.args[1]?.type === 'number' &&
    value.args[0].value > value.args[1].value
  ) {
    return { ok: false }
  }
  return { ok: true, value }
}

export function NumberField({
  label,
  value,
  unit,
  onValue,
  clearable,
  micro,
  boxed,
  sugared,
  tips,
  autoFocus,
  placeholder,
}: {
  label: string
  /** The slot as the script writes it — `''` when the slot is empty. */
  value: string
  unit: Unit
  /** Null only ever arrives when `clearable` — an emptied field removes what it held. */
  onValue: (value: Expr | null) => void
  clearable?: boolean
  /** Wear the label as a micro-caption underneath, the way a set line's row does. */
  micro?: boolean
  /** Wear a filled box, the way a form does — a row is chromeless, an editor is not. */
  boxed?: boolean
  /** Understand `8-12` as a range and `5+` as an amrap — the primary slot's shorthand. */
  sugared?: boolean
  /** What can be typed here, shown in a tip while the field has focus. */
  tips?: string
  autoFocus?: boolean | undefined
  placeholder?: string
}) {
  const [text, setText] = useState(value)
  const [bad, setBad] = useState(false)
  const [focused, setFocused] = useState(false)
  // What this field last wrote, as the script writes it back. The one change that comes home
  // in that form is this field's own echo and must not clobber the typing that made it;
  // anything after — an undo, a preset switch, the script panel — replaces what is shown.
  const [own, setOwn] = useState<string | null>(null)
  // What the slot held when the field was entered, which is what Escape goes back to.
  const origin = useRef(value)
  const select = useSelectOnFocus()
  const [seen, setSeen] = useState(value)
  if (seen !== value) {
    setSeen(value)
    if (own !== value) {
      setText(value)
      setBad(false)
    }
    setOwn(null)
  }

  const field = (
    <span
      className={`relative inline-grid ${
        // A boxed field takes the room its row gives it, so a form's inputs all end level.
        boxed === true ? 'w-full min-w-0 flex-1' : 'max-w-44'
      }`}
    >
      <span
        aria-hidden
        className="invisible col-start-1 row-start-1 min-w-[2ch] whitespace-pre px-1 text-base"
      >
        {text === '' ? (placeholder ?? '—') : text}
      </span>
      <input
        aria-label={label}
        aria-invalid={bad || undefined}
        value={text}
        size={1}
        autoFocus={autoFocus}
        placeholder={placeholder ?? '—'}
        className={`col-start-1 row-start-1 w-full text-base tabular-nums outline-none transition-colors placeholder:text-ink-muted ${
          boxed === true
            ? 'rounded-lg bg-surface-raised px-2 py-1 text-left text-ink'
            : `rounded-lg bg-transparent px-1 py-0.5 text-center focus:bg-surface-raised ${
                micro === true ? '' : 'hover:bg-hover'
              }`
        } ${bad ? 'ring-1 ring-danger' : 'focus:ring-1 focus:ring-accent'}`}
        onChange={(event) => {
          const changed = event.target.value
          setText(changed)
          const meant = checked(changed, unit, clearable === true, sugared === true)
          if (meant.ok) {
            setBad(false)
            setOwn(exprText(meant.value, sugared === true))
            onValue(meant.value ?? null)
          } else {
            // An empty field on its way somewhere is not worth flagging; text the script
            // cannot take is, so a typo turns red while it is still under the caret.
            setBad(changed.trim() !== '')
          }
        }}
        onFocus={(event) => {
          origin.current = value
          setFocused(true)
          select.onFocus(event)
        }}
        onMouseUp={select.onMouseUp}
        onBlur={() => {
          setFocused(false)
          const meant = checked(text, unit, clearable === true, sugared === true)
          if (meant.ok) {
            setText(exprText(meant.value, sugared === true))
          } else {
            // Left holding something the script cannot take, it settles back to what is in force.
            setText(value)
          }
          setBad(false)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur()
          } else if (event.key === 'Escape') {
            // Escape means "as it was when I got here", however many keystrokes ago that was.
            // It is spent on the field — it does not also close the editor around it.
            event.stopPropagation()
            const meant = checked(origin.current, unit, clearable === true, sugared === true)
            if (meant.ok) {
              setOwn(exprText(meant.value, sugared === true))
              onValue(meant.value ?? null)
            }
            setText(origin.current)
            setBad(false)
          }
        }}
      />
      <FloatOut open={(focused || bad) && tips !== undefined}>
        <span
          aria-hidden
          className={`absolute left-0 top-full z-30 mt-1 w-max max-w-52 whitespace-normal rounded-lg bg-surface-bright px-2 py-1 text-[0.6875rem] shadow-xl ring-1 ring-white/10 ${
            bad ? 'text-danger' : 'text-ink-muted'
          }`}
        >
          {tips}
        </span>
      </FloatOut>
    </span>
  )

  if (micro === true) {
    return <MicroField label={label}>{field}</MicroField>
  }
  return field
}

/**
 * A committed value as the field shows it. A sugared field wears a range as `8-12` and an
 * amrap as `5+` — the same shorthand its owner hands back — so its own echo is recognised
 * as its own, and never clobbers the typing that made it.
 */
function exprText(value: Expr | null | undefined, sugared: boolean): string {
  if (value === null || value === undefined) {
    return ''
  }
  if (sugared && value.type === 'call' && value.callee.type === 'name') {
    const [first, second] = value.args
    if (value.callee.name === 'range' && first?.type === 'number' && second?.type === 'number') {
      return `${exprSource(first)}-${exprSource(second)}`
    }
    if (value.callee.name === 'amrap' && first?.type === 'number') {
      return `${exprSource(first)}+`
    }
  }
  return exprSource(value)
}

/**
 * How tall the line a row's values sit on is: one line of text and the input's own padding.
 * Everything else in the row — the glyphs between the fields, the marks beside them — is held
 * to that height so it lands on the values rather than on the middle of the whole column.
 */
const VALUE_LINE = 'h-[1.85rem]'

/**
 * A value with its name in small print underneath, so a row of numbers reads at a glance.
 * The name is part of the field: the pair share one hover, and pressing either lands in the
 * input — which a label wrapping its input gives away for free. The hover stands off the
 * name on both sides, so a long one is never wearing the box as a border.
 */
export function MicroField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex shrink-0 cursor-text flex-col items-center rounded-lg px-1.5 pb-0.5 transition-colors hover:bg-hover">
      {children}
      <span aria-hidden className="text-[0.6875rem] uppercase tracking-wider text-ink-muted/80">
        {label}
      </span>
    </label>
  )
}

/** The glyph between a row's fields, sitting on the line the values sit on. */
export function FieldSep({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden className={`flex shrink-0 items-center px-1.5 text-ink-muted ${VALUE_LINE}`}>
      {children}
    </span>
  )
}

/**
 * A card row's editor, floated beside the card rather than inside it — a board that scrolls
 * sideways would otherwise cut it off at the column's edge. It anchors to the row it belongs
 * to, opens to whichever side has the room, and closes when the pointer lands outside it or
 * Escape says so.
 */
export function RowPopover({
  open,
  label,
  onClose,
  children,
}: {
  open: boolean
  label: string
  onClose: () => void
  children: ReactNode
}) {
  const anchor = useRef<HTMLDivElement>(null)

  /** Whether something is part of the row this popover belongs to, or of the undo controls. */
  const ours = (target: EventTarget | null): boolean => {
    const element = target as Element | null
    if (element?.closest('[data-undo-control]')) {
      return true
    }
    const row = anchor.current?.parentElement
    return row !== null && row !== undefined && element !== null && row.contains(element)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
        }
      }}
    >
      {/* The row itself is what the popover hangs off, without a box of its own to add. */}
      <PopoverAnchor ref={anchor} className="pointer-events-none absolute inset-0" />
      <PopoverContent
        role="dialog"
        aria-label={label}
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        // As tall as the room beside the card allows, up to what the form actually needs.
        className="flex max-h-[min(36rem,var(--radix-popover-content-available-height))] w-96 max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0"
        // The row the popover hangs under stays fair game — its own fields, its chevron. So do
        // undo and redo: taking an edit back is part of editing, not a reason to lose the spot.
        keepsOpenOn={ours}
        onFocusOutside={(event) => {
          if (ours(event.target)) {
            event.preventDefault()
          }
        }}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

/** A small on-or-off pill — warm-up, film it — that shows its state rather than a tick. */
export function TogglePill({
  label,
  active,
  onToggle,
  icon,
}: {
  label: string
  active: boolean
  onToggle: () => void
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${
        active ? 'bg-accent/15 text-accent' : 'bg-surface-raised text-ink-muted hover:text-ink'
      }`}
      onClick={onToggle}
    >
      {icon}
      {label}
    </button>
  )
}

/** One of a row of choices, worn as a chip. */
export function ChoiceChip({
  label,
  active,
  onPick,
  title,
}: {
  label: string
  active: boolean
  onPick: () => void
  title?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      className={`rounded-full px-3 py-1 text-xs transition-colors ${
        active ? 'bg-accent text-accent-ink' : 'bg-surface-raised text-ink-muted hover:text-ink'
      }`}
      onClick={onPick}
    >
      {label}
    </button>
  )
}

/**
 * A labelled row inside an opened editor: name on the left, control on the right. Every row
 * hangs its name off the same edge and gives the control the same room, so a form of them
 * reads as a column of fields rather than a pile of differently sized boxes.
 */
export function PanelRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex items-start gap-3 text-sm text-ink-muted">
      <span className="w-24 shrink-0 pt-1.5">{label}</span>
      <div className="min-w-0 flex-1 space-y-1">{children}</div>
    </label>
  )
}

/** The same row for a control that is not one input, which is more than a label may own. */
export function PanelBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm text-ink-muted">
      <span className="w-24 shrink-0 pt-1.5">{label}</span>
      <div className="min-w-0 flex-1 space-y-2">{children}</div>
    </div>
  )
}
